'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { ReactNode, useState } from 'react';

import { useV1Currencies } from '@flipster/features/src/shared/currency/use-v1-currencies';
import { useConvertModal } from '@flipster/features/src/shared/wallet-modal/convert';
import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { Button } from '@flipster/react/design-system/button';
import { Modal } from '@flipster/react/design-system/modal';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import Num from '@flipster/utils/num';

import { useLaunchpoolById } from '../_hooks/use-launchpool-by-id';

type SelectActionType = 'deposit' | 'convert';

const ActionRadioItems: { label: ReactNode; value: SelectActionType }[] = [
  { label: 'Deposit', value: 'deposit' },
  { label: 'Convert', value: 'convert' },
];

interface Props {
  launchpoolId: number;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

const SelectActionModal = ({ launchpoolId, isOpen, onClose, onExit }: Props) => {
  const t = useTranslations();
  const [selected, setSelected] = useState<SelectActionType | undefined>(undefined);
  const openDepositModal = useDepositModal();
  const openConvertModal = useConvertModal();

  const { walletsAtom } = useBunja(walletsBunja);
  const wallet = useAtomValue(walletsAtom);
  const { currenciesKeyByCurrency } = useV1Currencies();
  const currentLaunchpool = useLaunchpoolById(launchpoolId);

  const stakingCurrency = currentLaunchpool?.stakingCurrency || 'unknown';
  const availableAmount = wallet?.[stakingCurrency]?.walletBalanceExclLocked;
  const stakingCurrencyPrecision = currenciesKeyByCurrency?.[stakingCurrency]?.blockchainInfo[0]?.withdrawalPrecision;

  const sortedCurrenciesByAmount = Object.entries(wallet || {})
    .filter(([currency]) => currency !== stakingCurrency) // 현재 currency는 제외함
    .sort(([, a], [, b]) => {
      return Num(b.walletBalanceWithdrawableInUsdt).minus(a.walletBalanceWithdrawableInUsdt).toNumber();
    })
    .map(([currency]) => currency);
  const largestAmountCurrency = sortedCurrenciesByAmount.length > 0 ? sortedCurrenciesByAmount[0] : 'USDT';

  const handleClickClose = () => {
    onClose();
    setSelected(undefined);
  };

  const onClickContinue = () => {
    if (!selected) return;
    handleClickClose();
    if (selected === 'deposit') openDepositModal();
    if (selected === 'convert') {
      openConvertModal({ defaultValues: { currency: largestAmountCurrency, toCurrency: stakingCurrency } });
    }
  };

  return (
    <Modal.Root isOpen={isOpen} size='small' onClose={handleClickClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title
          label={t('Autogen_common.r251126.s_Add_stakingCurrency', {
            stakingCurrency: stakingCurrency,
          })}
        />
      </Modal.Header>
      <Modal.Content>
        <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3', mb: 16 })}>
          Available:{' '}
          <Formatted.Number value={availableAmount} suffix={stakingCurrency} precision={stakingCurrencyPrecision} />
        </p>
        <fieldset
          className={vstack({
            gap: 16,
            alignItems: 'flex-start',
          })}
        >
          {ActionRadioItems.map((item) => (
            <RadioButton
              key={item.value}
              value={item.value}
              checked={selected === item.value}
              onChange={() => setSelected(item.value)}
              css={{ textStyle: 'Text/L/Regular', color: 'content.default.level-5', alignItems: 'center' }}
            >
              {item.label}
            </RadioButton>
          ))}
        </fieldset>
      </Modal.Content>
      <Modal.Footer>
        <Button
          size='large'
          hierarchy='primary'
          label={t('Autogen_common.r251126.s_Continue')}
          color='accent'
          disabled={!selected}
          onClick={onClickContinue}
        />
      </Modal.Footer>
    </Modal.Root>
  );
};

export default SelectActionModal;
